import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EfInput } from '../shared/ef-input.interface';
import { EfFormFieldSuffixDirective } from './directives/suffix.directive';
import { EfFormFieldPrefixDirective } from './directives/prefix.directive';
import { map, Observable, startWith } from 'rxjs';
import { FORM_ERRORS_FORMATTER } from './error-formatter/error-formatter';
import { TranslateModule } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'ef-form-field',
  standalone: true,
  imports: [CommonModule, AsyncPipe, TranslateModule],
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-active]': 'isActive()',
    '[class.is-filled]': 'isFilled()',
    '[class.is-invalid]': 'isInvalid()',
    '[class.is-touched]': 'isTouched()',
    '[class.is-hover]': 'isHover()',
    class: 'block relative my-4',
  },
  templateUrl: './form-field.component.html',
})
export class EfFormFieldComponent implements AfterContentInit, AfterViewInit {
  public label = input<string>();
  public autoLabeling = input<boolean>(true);

  public inputComponentControl = contentChild.required(EfInput, {
    read: NgControl,
  });
  public inputComponentEl = contentChild.required(EfInput);

  public formFieldSuffix = contentChild(EfFormFieldSuffixDirective);
  public formFieldPrefix = contentChild(EfFormFieldPrefixDirective);

  public inputId = signal<string>('');

  public isActive = signal<boolean>(false);
  public isFilled = signal<boolean>(false);
  public isInvalid = signal<boolean>(false);
  public isTouched = signal<boolean>(false);
  public isHover = signal<boolean>(false);

  public errors$?: Observable<string>;

  private readonly errorsFormatter = inject(FORM_ERRORS_FORMATTER, {
    optional: true,
  });

  public ngAfterContentInit(): void {
    if (this.errorsFormatter) {
      const formatter = this.errorsFormatter;

      this.errors$ = this.inputComponentControl().statusChanges?.pipe(
        startWith(this.inputComponentControl().errors),
        map(() => formatter.format(this.inputComponentControl())),
      );
    }

    const inputComponentControl = this.inputComponentControl();
    const inputComponentEl = this.inputComponentEl();

    const _superTouched = inputComponentControl.control?.markAsTouched;

    if (_superTouched && inputComponentControl.control) {
      inputComponentControl.control.markAsTouched = (...args) => {
        _superTouched.call(inputComponentControl.control, ...args);

        this.isTouched.set(true);
      };
    }

    inputComponentControl.valueChanges
      ?.pipe(startWith(inputComponentControl.value), untilDestroyed(this))
      .subscribe((value) => {
        this.isFilled.set(value != null && value != '');
      });

    inputComponentControl.statusChanges
      ?.pipe(startWith(inputComponentControl.status), untilDestroyed(this))
      .subscribe((status) => {
        this.isInvalid.set(status === 'INVALID');
      });

    inputComponentEl.onFocus.pipe(untilDestroyed(this)).subscribe(() => {
      this.isActive.set(true);
    });

    inputComponentEl.onBlur.pipe(untilDestroyed(this)).subscribe(() => {
      this.isActive.set(false);
      this.isTouched.set(true);
    });

    inputComponentEl.onMouseEnter.pipe(untilDestroyed(this)).subscribe(() => {
      this.isHover.set(true);
    });

    inputComponentEl.onMouseLeave.pipe(untilDestroyed(this)).subscribe(() => {
      this.isHover.set(false);
    });
  }

  public ngAfterViewInit(): void {
    if (!this.inputComponentEl().elementRef.nativeElement.id && this.autoLabeling()) {
      const name = this.inputComponentEl().elementRef.nativeElement.name || this.inputComponentControl().name;

      this.inputComponentEl().elementRef.nativeElement.id = name + Math.random().toString(36).slice(2, 7);
    }

    this.inputId.set(this.inputComponentEl().elementRef.nativeElement.id);
  }
}
